import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Fab from "@mui/material/Fab";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import AuthContext from "../auth/AuthContext";
import Post from "../components/Post";
import PostFormDialog from "../components/PostFormDialog";
import CommentsSection from "../components/CommentsSection";
import {
  createRecipeVariant,
  deleteRecipe,
  getRecipe,
  preferRecipe,
  updateRecipe,
} from "../utils/apiClient";
import useDocumentTitle from "../hooks/useDocumentTitle";

function RecipeDetail() {
  const params = useParams();
  const recipeRef = params.recipeRef || params["*"] || "";
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [variantOpen, setVariantOpen] = useState(false);
  const [preferring, setPreferring] = useState(false);

  const ref = decodeURIComponent(recipeRef);
  const isAdmin = !!user?.is_admin;
  const signedIn = !!user?.user_uuid;
  const variants = Array.isArray(recipe?.variants) ? recipe.variants : [];
  const familyRootRef = recipe?.root_recipe_uuid || recipe?.permalink;
  const showPrefer = signedIn && !recipe?.is_preferred;

  useDocumentTitle(recipe?.title || "");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ref) {
        setLoading(false);
        setError("Opskrift ikke fundet");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const data = await getRecipe(ref);
        if (!cancelled) setRecipe(data);
      } catch (err) {
        if (!cancelled) {
          const detail = err.response?.data?.detail;
          setError(typeof detail === "string" ? detail : err.message || "Kunne ikke hente opskrift");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ref]);

  const handleEditSave = async (payload) => {
    const updated = await updateRecipe(recipe.permalink || recipe.recipe_uuid, payload);
    setEditOpen(false);
    if (updated.permalink && updated.permalink !== recipe.permalink) {
      navigate(`/recipes/${updated.permalink}`, { replace: true });
    } else {
      setRecipe(updated);
    }
  };

  const handleEditDelete = async () => {
    await deleteRecipe(recipe.permalink || recipe.recipe_uuid);
    setEditOpen(false);
    navigate("/recipes");
  };

  const handleVariantSave = async (payload) => {
    const created = await createRecipeVariant(recipe.permalink || recipe.recipe_uuid, payload);
    setVariantOpen(false);
    navigate(`/recipes/${created.permalink || created.recipe_uuid}`);
  };

  const handlePrefer = async () => {
    setPreferring(true);
    try {
      const updated = await preferRecipe(recipe.permalink || recipe.recipe_uuid);
      setRecipe(updated);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke sætte foretrukken");
    } finally {
      setPreferring(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error && !recipe) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button component={RouterLink} to="/recipes" variant="text">
          ← Opskrifter
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", pb: isAdmin ? 10 : 0 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mt: 2 }}
      >
        <Button component={RouterLink} to="/recipes" variant="text" sx={{ px: 0 }}>
          ← Opskrifter
        </Button>
        <Stack direction="row" spacing={1}>
          {showPrefer ? (
            <Button variant="outlined" onClick={handlePrefer} disabled={preferring}>
              {preferring ? "Gemmer…" : "Gør til foretrukken"}
            </Button>
          ) : null}
          {recipe?.is_preferred ? (
            <Button variant="outlined" disabled>
              Foretrukken
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {recipe?.variant_number ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Variant v{recipe.variant_number}
          {familyRootRef ? (
            <>
              {" · "}
              <Typography
                component={RouterLink}
                to={`/recipes?root=${encodeURIComponent(familyRootRef)}`}
                variant="body2"
                sx={{ color: "inherit", textDecoration: "underline" }}
              >
                Se alle varianter
              </Typography>
            </>
          ) : null}
        </Typography>
      ) : null}

      {error ? (
        <Typography color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      ) : null}

      <Post
        post={recipe}
        variant="detail"
        onEdit={isAdmin ? () => setEditOpen(true) : undefined}
      />

      {variants.length ? (
        <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
              Varianter
            </Typography>
            {familyRootRef ? (
              <Button
                component={RouterLink}
                to={`/recipes?root=${encodeURIComponent(familyRootRef)}`}
                size="small"
              >
                Se alle
              </Button>
            ) : null}
          </Stack>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              pb: 1,
              mx: -1,
              px: 1,
              scrollSnapType: "x mandatory",
            }}
          >
            {variants.map((v) => (
              <Box
                key={v.recipe_uuid}
                sx={{
                  minWidth: { xs: 260, sm: 300 },
                  maxWidth: 340,
                  flex: "0 0 auto",
                  scrollSnapAlign: "start",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  px: 2,
                  bgcolor: "background.paper",
                }}
              >
                <Post
                  post={v}
                  linkTo={`/recipes/${v.permalink || v.recipe_uuid}`}
                />
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      <CommentsSection kind="recipe" contentRef={recipe?.permalink || recipe?.recipe_uuid} />

      {isAdmin ? (
        <Fab
          color="primary"
          aria-label="Lav variant af opskrift"
          onClick={() => setVariantOpen(true)}
          sx={{
            position: "fixed",
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <Badge
            badgeContent="1"
            color="secondary"
            overlap="circular"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <RestaurantMenuIcon />
          </Badge>
        </Fab>
      ) : null}

      <PostFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSubmit={handleEditSave}
        onDelete={handleEditDelete}
        title="Rediger opskrift"
        submitLabel="Gem"
        formKey={recipe?.recipe_uuid || "edit"}
        recipeUuid={recipe?.recipe_uuid}
        postType="recipe"
        hideTemplate
        initial={{
          title: recipe?.title || "",
          body: recipe?.body || "",
          instructions: recipe?.instructions || "",
          yield_amount: recipe?.yield_amount || "",
          cook_time: recipe?.cook_time || "",
          photos: recipe?.photos || [],
          visibility: recipe?.visibility || "private",
          composition: "recipe",
          tags: recipe?.tags || [],
          ingredient_groups: recipe?.ingredient_groups || [],
        }}
      />

      <PostFormDialog
        open={variantOpen}
        onClose={() => setVariantOpen(false)}
        onSubmit={handleVariantSave}
        title="Ny opskrift fra opskrift"
        submitLabel="Gem variant"
        formKey={`variant-${recipe?.recipe_uuid || "new"}`}
        postType="recipe"
        hideTemplate
        initial={{
          title: recipe?.title || "",
          body: recipe?.body || "",
          instructions: recipe?.instructions || "",
          yield_amount: recipe?.yield_amount || "",
          cook_time: recipe?.cook_time || "",
          photos: recipe?.photos || [],
          visibility: recipe?.visibility || "private",
          composition: "recipe",
          tags: recipe?.tags || [],
          ingredient_groups: recipe?.ingredient_groups || [],
        }}
      />
    </Box>
  );
}

export default RecipeDetail;
